import UIMetrics from "core/telemetry/UIMetrics";

import { CLONE_DATABASE_SETUP_PROMPT } from "./databaseSetupPrompt";
import { setPendingClarkPrompt } from "./pendingPrompt";

export type CloneDatabaseSetupStage = "failed" | "queued" | "started";

// Cloning must succeed even if telemetry is not running: the dashboard bundle
// initialises it separately from the editor, and `getTelemetry` throws when it
// has not been initialised yet.
export function recordCloneDatabaseSetup(stage: CloneDatabaseSetupStage): void {
  try {
    UIMetrics.recordCloneDatabaseSetup(stage);
  } catch {
    // Counting the turn is not worth failing the clone or the turn over.
  }
}

/**
 * Queue the database setup turn a cloned app should send the first time it is
 * opened. Returns the nonce the editor URL must carry as `?clarkPrompt=`, or
 * undefined when nothing was queued.
 *
 * The clone lands in the editor through a full page load, so the request has to
 * survive it; `setPendingClarkPrompt` keeps it in localStorage until the editor
 * takes the nonce once. Comparing the `queued` and `started` counts shows how
 * often the handoff is lost, which is the known weakness of doing this in the
 * browser - so a browser that refused the write is not counted as queued, or
 * that gap would mix storage failures in with navigation losses.
 */
export function queueCloneDatabaseSetup(clone: {
  id: string;
  requiresDatabaseSetup?: boolean;
}): string | undefined {
  if (!clone.requiresDatabaseSetup) {
    return undefined;
  }
  const nonce = crypto.randomUUID();
  const stored = setPendingClarkPrompt({
    applicationId: clone.id,
    autoSubmit: true,
    nonce,
    prompt: CLONE_DATABASE_SETUP_PROMPT,
  });
  if (!stored) {
    return undefined;
  }
  recordCloneDatabaseSetup("queued");
  return nonce;
}
